import type { AuthClient } from './auth';
import type { BuildingPanel } from './building';
import type { PlacedObject } from './content';
import type { Member } from './identity';
import type { WorldMenu } from './menu';
import type { Connection } from './network';
import { RadialMenu, type RadialAction } from './radial-menu';
import type { ContextTarget, Viewport } from './viewport';

/** Context actions use the same authoritative paths as the Build and Interactions dialogs. */
export class ContextActions {
  private radial: RadialMenu;
  private target: ContextTarget | null = null;
  private epoch = '';
  private profile = document.createElement('dialog');
  private profileMember: Member | null = null;

  constructor(private connection: Connection, private auth: AuthClient, private viewport: Viewport, private building: BuildingPanel, private menu: WorldMenu, private canvas: HTMLCanvasElement, stopInput: () => void) {
    this.radial = new RadialMenu(canvas.parentElement!, canvas, stopInput);
    this.profile.id = 'avatar-profile-dialog'; this.profile.className = 'avatar-profile'; this.profile.setAttribute('aria-labelledby', 'avatar-profile-title');
    this.profile.innerHTML = '<form method="dialog"><button class="close-dialog" aria-label="Close profile">Close</button></form><h2 id="avatar-profile-title">Profile</h2><div class="profile-monogram" aria-hidden="true"></div><h3 class="profile-name"></h3><span class="profile-role"></span><p class="profile-location"></p>';
    document.body.append(this.profile);
    this.profile.addEventListener('close', () => { this.profileMember = null; if (connection.state.epoch) canvas.focus(); });
    viewport.onContext = (target, x, y) => {
      this.radial.close(false); this.target = target;
      if (!target || !connection.state.epoch) return;
      const member = this.member(), object = this.object();
      const title = target.kind === 'avatar' ? member?.displayName : object?.value.name;
      if (!title) return;
      viewport.releasePointerLock();
      this.radial.open(x, y, title, target.kind === 'avatar' ? 'Avatar' : 'Object', this.actions());
    };
  }

  private member(): Member | undefined { return this.connection.state.members.find(member => member.entityId === this.target?.id); }
  private object(): PlacedObject | undefined { return this.connection.state.content?.objects.find(object => object.id === this.target?.id); }

  private sitUnavailable(object: PlacedObject): string | undefined {
    const c = this.connection, user = this.auth.state?.user, self = c.state.entities.get(c.state.selfId);
    if (!self || !c.state.epoch) return 'Connect to the world to sit.';
    if (!user || user.role === 'guest' || user.muted) return 'Sitting is unavailable for this account.';
    if (c.ownInteraction || c.interactionStarting) return 'Stand or finish your current interaction first.';
    if (Math.hypot(self.position.x - object.value.position.x, self.position.z - object.value.position.z) > 3) return 'Move within 3 m to sit here.';
    if (c.interactions.some(item => item.objectId === object.id)) return 'This seat is occupied.';
    const edit = c.edits.get(object.id);
    if (edit && edit.expiresMs > c.serverTime()) return 'This object is being edited.';
    const value = object.value, height = value.position.y + value.scale.y / 2;
    if (Math.abs(value.rotation[0]) + Math.abs(value.rotation[2]) > 1e-6 || height < .4 || height > 1.2 || value.scale.x < .5 || value.scale.x > 2 || value.scale.z < .5 || value.scale.z > 2) return 'This object’s size or tilt does not support sitting.';
  }

  private actions(): RadialAction[] {
    if (this.target?.kind === 'avatar') return [{ id: 'profile', label: 'Profile', icon: 'profile', run: () => { const member = this.member(); if (member) this.showProfile(member); } }];
    const object = this.object(); if (!object) return [];
    const actions: RadialAction[] = [];
    if (object.value.assetId === 'builtin_cube_v1') actions.push({
      id: 'sit', label: 'Sit here', icon: 'sit', unavailable: this.sitUnavailable(object),
      run: () => {
        const current = this.object(); if (!current || this.sitUnavailable(current)) return;
        this.menu.open('interactions-panel', false);
        const select = document.getElementById('seat-select') as HTMLSelectElement; select.value = current.id;
        this.connection.interact('sit', current.id, 0, this.connection.selectedInteraction?.kind === 'seat' ? this.connection.selectedInteraction.id : '');
        this.canvas.focus();
      },
    });
    actions.push({ id: 'edit', label: 'Edit', icon: 'edit', unavailable: this.building.editUnavailable(object.id), run: () => {
      const current = this.object(); if (!current || this.building.editUnavailable(current.id)) return;
      this.menu.open('build-panel', false); this.building.edit(current.id);
      document.getElementById('build-tools')?.scrollIntoView({ block: 'nearest' });
    } });
    return actions;
  }

  private showProfile(member: Member): void {
    this.profileMember = member;
    this.profile.querySelector('.profile-monogram')!.textContent = Array.from(member.displayName).slice(0, 2).join('').toLocaleUpperCase();
    this.profile.querySelector('.profile-name')!.textContent = member.displayName;
    this.profile.querySelector('.profile-role')!.textContent = member.role;
    this.profileLocation(); this.profile.showModal();
  }
  private profileLocation(): void {
    const present = this.connection.state.members.some(member => member.entityId === this.profileMember?.entityId);
    this.profile.querySelector('.profile-location')!.textContent = present ? `In ${this.connection.state.content?.room.settings.name ?? 'this room'}` : 'No longer in this room';
  }

  render(): void {
    const c = this.connection;
    if (this.epoch !== c.state.epoch) { this.epoch = c.state.epoch; this.target = null; this.radial.close(false); this.profile.close(); }
    if (this.profile.open) this.profileLocation();
    if (!this.radial.isOpen) return;
    if (!this.target || (this.target.kind === 'avatar' ? !this.member() || !c.state.entities.has(this.target.id) : !this.object())) { this.radial.close(); this.target = null; return; }
    this.radial.update(this.actions());
  }

  dispose(): void { this.viewport.onContext = () => {}; this.radial.dispose(); this.profile.remove(); }
}
